import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { TFunction } from 'i18next';

import PencilLine from '@/assets/icons/pencil_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Caption } from '@/components/atoms/Eyebrow';
import type { StockingCycle } from '@/domain/stockingCycles';
import { getIsoWeek, getIsoWeekYear, now } from '@/utils/date';

import type { BoardCycle, LastClosure } from '../../types';
import Glyph from '../Glyph/Glyph';

import styles from './CycleBanner.module.scss';

interface CycleBannerProps {
    cycle: BoardCycle;
    /** From the cycle's timeline; without it the last close reads as a closure, not a harvest. */
    lastClosure?: LastClosure | null;
    /** Opens Edit stocking from the title; without it (demo mode) the title is plain text. */
    onEdit?: () => void;
}

/** The cycle's derived status; fallowing is a completed cycle whose site lies empty. */
export type BannerStatus = StockingCycle['status'] | 'fallowing';

export function bannerStatus(cycle: StockingCycle): BannerStatus {
    return cycle.status === 'completed' && cycle.isFallowing ? 'fallowing' : cycle.status;
}

const RANGE_KEYS = {
    stocked: {
        week: 'board.banner.stockedWeek',
        weeks: 'board.banner.stockedWeeks',
        acrossYears: 'board.banner.stockedWeeksAcrossYears',
    },
    planned: {
        week: 'board.banner.plannedWeek',
        weeks: 'board.banner.plannedWeeks',
        acrossYears: 'board.banner.plannedWeeksAcrossYears',
    },
    fallowing: {
        week: 'board.banner.fallowingWeek',
        weeks: 'board.banner.fallowingWeeks',
        acrossYears: 'board.banner.fallowingWeeksAcrossYears',
    },
} as const;

/** ISO weeks and ISO week-years: 29 Dec 2025 is week 1 of 2026. */
function weekRange(
    t: TFunction,
    keys: (typeof RANGE_KEYS)[keyof typeof RANGE_KEYS],
    first: Date,
    last: Date,
    status?: string
): string {
    const from = getIsoWeek(first);
    const to = getIsoWeek(last);
    const fromYear = getIsoWeekYear(first);
    const toYear = getIsoWeekYear(last);
    if (fromYear !== toYear) return t(keys.acrossYears, { status, from, fromYear, to, toYear });
    return from === to
        ? t(keys.week, { status, week: from, year: toYear })
        : t(keys.weeks, { status, from, to, year: toYear });
}

function stockingFacts(t: TFunction, cycle: StockingCycle, status: string): string {
    if (cycle.stockedFrom) {
        const first = cycle.stockedFrom;
        const last = cycle.stockedTo ?? first;
        // A group can be registered ahead of its stocking, so part of the range may be to come.
        const today = now();
        if (first <= today && last > today) {
            return t('board.banner.stockedAndPlanned', {
                status,
                from: getIsoWeek(first),
                fromYear: getIsoWeekYear(first),
                to: getIsoWeek(last),
                toYear: getIsoWeekYear(last),
            });
        }
        return weekRange(t, RANGE_KEYS[first > today ? 'planned' : 'stocked'], first, last, status);
    }
    if (cycle.plannedStartDate) {
        return t('board.banner.plannedWeek', {
            status,
            week: getIsoWeek(cycle.plannedStartDate),
            year: getIsoWeekYear(cycle.plannedStartDate),
        });
    }
    return status;
}

/**
 * After the last group closed: when that was, a harvest only if the timeline says so, and the
 * site's fallowing since or until the next stocking.
 */
function closingFacts(
    t: TFunction,
    cycle: StockingCycle,
    lastClosure: LastClosure | null
): string[] {
    const closed = cycle.lastClosedAt;
    if (cycle.status !== 'completed' || !closed) return [];
    const harvested =
        lastClosure?.harvest === true && lastClosure.at.getTime() === closed.getTime();
    const facts = [
        t(harvested ? 'board.banner.lastHarvest' : 'board.banner.lastClosure', {
            week: getIsoWeek(closed),
            year: getIsoWeekYear(closed),
        }),
    ];
    if (cycle.fallowingUntil) {
        facts.push(weekRange(t, RANGE_KEYS.fallowing, closed, cycle.fallowingUntil));
    } else if (cycle.isFallowing) {
        facts.push(
            t('board.banner.fallowingSince', {
                week: getIsoWeek(closed),
                year: getIsoWeekYear(closed),
            })
        );
    }
    return facts;
}

function useCycleFacts(cycle: StockingCycle, lastClosure: LastClosure | null): string {
    const { t } = useTranslation('fishGroups');
    const status = t(`board.cycleStatus.${bannerStatus(cycle)}`);
    return [stockingFacts(t, cycle, status), ...closingFacts(t, cycle, lastClosure)].join(
        t('board.banner.separator')
    );
}

/** In place of a cycle's banner when the board shows the groups that belong to no cycle. */
export function UnassignedBanner() {
    const { t } = useTranslation('fishGroups');

    return (
        <div className={styles.banner}>
            <Caption className={styles.title}>{t('board.banner.unassignedTitle')}</Caption>
            <span>{t('board.banner.unassignedFacts')}</span>
        </div>
    );
}

/** The selected cycle's name and facts above the board. */
export default function CycleBanner(props: CycleBannerProps) {
    const { cycle, lastClosure = null, onEdit } = props;
    const { t } = useTranslation('fishGroups');
    const facts = useCycleFacts(cycle, lastClosure);

    return (
        <div className={classNames(styles.banner, styles[`banner--${bannerStatus(cycle)}`])}>
            {onEdit ? (
                <Button
                    variant="ghost"
                    size="small"
                    className={styles.edit}
                    iconEnd={<Glyph icon={PencilLine} />}
                    aria-label={t('editStocking.open', { name: cycle.displayName })}
                    onClick={onEdit}>
                    <Caption className={styles.title}>{cycle.displayName}</Caption>
                </Button>
            ) : (
                <Caption className={styles.title}>{cycle.displayName}</Caption>
            )}
            <span className={styles.facts}>{facts}</span>
        </div>
    );
}
